import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc 
} from 'firebase/firestore';

/**
 * Service pour récupérer les intervenants éligibles pour un bénéficiaire
 * Critères d'éligibilité :
 * 1. Intervenants ayant des rendez-vous (passés ou futurs) avec le bénéficiaire
 * 2. Intervenants avec droits de gestion des bénéficiaires
 * 3. Intervenants avec qui il y a déjà eu des conversations
 */
export const getEligibleIntervenants = async (db, clientId) => {
  try {
    console.log(`🔍 Recherche des intervenants éligibles pour le bénéficiaire ${clientId}`);
    
    const eligibleIntervenants = new Map();

    // 1. Récupérer les intervenants via les rendez-vous
    const intervenantsFromAppointments = await getIntervenantsFromAppointments(db, clientId);
    intervenantsFromAppointments.forEach(intervenant => {
      eligibleIntervenants.set(intervenant.id, {
        ...intervenant,
        eligibilityReason: 'Rendez-vous avec le bénéficiaire'
      });
    });

    // 2. Récupérer les intervenants avec droits de gestion des bénéficiaires
    const intervenantsWithPermissions = await getIntervenantsWithClientManagementPermissions(db);
    intervenantsWithPermissions.forEach(intervenant => {
      if (!eligibleIntervenants.has(intervenant.id)) {
        eligibleIntervenants.set(intervenant.id, {
          ...intervenant,
          eligibilityReason: 'Droits de gestion des bénéficiaires'
        });
      }
    });

    // 3. Récupérer les intervenants via les conversations existantes
    const intervenantsFromConversations = await getIntervenantsFromConversations(db, clientId);
    intervenantsFromConversations.forEach(intervenant => {
      if (!eligibleIntervenants.has(intervenant.id)) {
        eligibleIntervenants.set(intervenant.id, {
          ...intervenant,
          eligibilityReason: 'Conversation existante'
        });
      }
    });

    const result = Array.from(eligibleIntervenants.values());
    console.log(`✅ ${result.length} intervenants éligibles trouvés pour le bénéficiaire ${clientId}`);
    
    return result;
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des intervenants éligibles:', error);
    return [];
  }
};

/**
 * Récupère les intervenants ayant des rendez-vous avec le bénéficiaire
 */
const getIntervenantsFromAppointments = async (db, clientId) => {
  try {
    const appointmentsRef = collection(db, 'appointments');
    const q = query(
      appointmentsRef,
      where('clientId', '==', clientId)
    );

    const querySnapshot = await getDocs(q);
    const employeeIds = new Set();

    querySnapshot.forEach(doc => {
      const appointment = doc.data();
      if (appointment.employeeId) {
        employeeIds.add(appointment.employeeId);
      }
    });

    // Récupérer tous les rôles pour obtenir leurs noms
    const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
    const rolesMap = new Map();
    rolesSnapshot.forEach(doc => {
      const roleData = doc.data();
      rolesMap.set(doc.id, roleData.name || doc.id);
    });

    // Récupérer les détails des employés
    const intervenants = [];
    for (const employeeId of employeeIds) {
      const employeeDoc = await getDoc(doc(db, 'employees', employeeId));
      if (employeeDoc.exists()) {
        const employeeData = employeeDoc.data();
        const roleId = employeeData.role;
        const roleName = rolesMap.get(roleId) || roleId || 'Collaborateur';

        intervenants.push({
          id: employeeId,
          firstName: employeeData.firstName || '',
          lastName: employeeData.lastName || '',
          name: `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim(),
          role: roleName,
          roleId: roleId,
          email: employeeData.email || '',
          photoURL: employeeData.photoURL || employeeData.profilePhoto || null,
          isActive: employeeData.isActive !== false
        });
      }
    }

    console.log(`📅 ${intervenants.length} intervenants trouvés via les rendez-vous`);
    return intervenants;
  } catch (error) {
    console.error('Erreur récupération intervenants via rendez-vous:', error);
    return [];
  }
};

/**
 * Récupère les intervenants avec droits de gestion des bénéficiaires
 */
const getIntervenantsWithClientManagementPermissions = async (db) => {
  try {
    // Récupérer tous les rôles pour identifier ceux avec permissions clients
    const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
    const rolesWithClientPermissions = new Set();
    const rolesMap = new Map();

    rolesSnapshot.forEach(doc => {
      const roleData = doc.data();
      rolesMap.set(doc.id, roleData.name || doc.id);

      // Vérifier si le rôle a des permissions de gestion clients
      if (roleData.permissions &&
          (roleData.permissions.client_management ||
           roleData.permissions.client_read ||
           roleData.permissions.client_write)) {
        rolesWithClientPermissions.add(doc.id);
      }
    });

    // Récupérer les employés avec ces rôles
    const employeesRef = collection(db, 'employees');
    const employeesSnapshot = await getDocs(employeesRef);
    const intervenants = [];

    employeesSnapshot.forEach(doc => {
      const employeeData = doc.data();
      if (rolesWithClientPermissions.has(employeeData.role) && employeeData.isActive !== false) {
        const roleId = employeeData.role;
        const roleName = rolesMap.get(roleId) || roleId || 'Collaborateur';

        intervenants.push({
          id: doc.id,
          firstName: employeeData.firstName || '',
          lastName: employeeData.lastName || '',
          name: `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim(),
          role: roleName,
          roleId: roleId,
          email: employeeData.email || '',
          photoURL: employeeData.photoURL || employeeData.profilePhoto || null,
          isActive: true
        });
      }
    });

    console.log(`👥 ${intervenants.length} intervenants avec droits de gestion clients`);
    return intervenants;
  } catch (error) {
    console.error('Erreur récupération intervenants avec permissions:', error);
    return [];
  }
};

/**
 * Récupère les intervenants avec qui le bénéficiaire a déjà eu des conversations
 */
const getIntervenantsFromConversations = async (db, clientId) => {
  try {
    const conversationsRef = collection(db, 'conversations');
    const q = query(
      conversationsRef,
      where('participants', 'array-contains', clientId),
      where('type', '==', 'client')
    );

    const querySnapshot = await getDocs(q);
    const employeeIds = new Set();

    querySnapshot.forEach(doc => {
      const conversation = doc.data();
      // Extraire les IDs d'employés des participants (exclure le client)
      conversation.participants.forEach(participantId => {
        if (participantId !== clientId) {
          employeeIds.add(participantId);
        }
      });
    });

    // Récupérer tous les rôles pour obtenir leurs noms
    const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
    const rolesMap = new Map();
    rolesSnapshot.forEach(doc => {
      const roleData = doc.data();
      rolesMap.set(doc.id, roleData.name || doc.id);
    });

    // Récupérer les détails des employés
    const intervenants = [];
    for (const employeeId of employeeIds) {
      const employeeDoc = await getDoc(doc(db, 'employees', employeeId));
      if (employeeDoc.exists()) {
        const employeeData = employeeDoc.data();
        const roleId = employeeData.role;
        const roleName = rolesMap.get(roleId) || roleId || 'Collaborateur';

        intervenants.push({
          id: employeeId,
          firstName: employeeData.firstName || '',
          lastName: employeeData.lastName || '',
          name: `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim(),
          role: roleName,
          roleId: roleId,
          email: employeeData.email || '',
          photoURL: employeeData.photoURL || employeeData.profilePhoto || null,
          isActive: employeeData.isActive !== false
        });
      }
    }

    console.log(`💬 ${intervenants.length} intervenants trouvés via les conversations`);
    return intervenants;
  } catch (error) {
    console.error('Erreur récupération intervenants via conversations:', error);
    return [];
  }
};

/**
 * Vérifie si un intervenant spécifique est éligible pour un bénéficiaire
 */
export const isIntervenantEligible = async (db, clientId, employeeId) => {
  try {
    const eligibleIntervenants = await getEligibleIntervenants(db, clientId);
    return eligibleIntervenants.some(intervenant => intervenant.id === employeeId);
  } catch (error) {
    console.error('Erreur vérification éligibilité intervenant:', error);
    return false;
  }
};
